import type { IUserPayload } from "../PermissionContext";

/**
 * A single access requirement that can be evaluated against a user.
 *
 * Matching semantics:
 * - By default (`requireAll` omitted / false) access is granted when the user
 *   matches ANY of the provided roles OR ANY of the provided permissions OR the
 *   custom `check` predicate returns `true` (logical OR across every supplied
 *   criterion).
 * - When `requireAll` is `true`, every supplied criterion must pass: the user
 *   must have ALL listed roles AND ALL listed permissions AND satisfy `check`.
 *
 * Roles and permissions support `*` wildcards (e.g. a granted `user:*` matches a
 * required `user:read`, and a granted `*` matches everything).
 */
export interface AccessQuery<
    Role extends string = string,
    Permission extends string = string
> {
    roles?: Role[] | Role;
    permissions?: Permission[] | Permission;
    /** Require every listed role AND permission (AND semantics) instead of ANY. */
    requireAll?: boolean;
    /** Custom predicate for rules that roles/permissions can't express. */
    check?: (user: IUserPayload) => boolean;
}

/** Normalizes a value that may be a single item, an array, or undefined. */
export const toArray = <T>(value?: T[] | T): T[] => {
    if (value === undefined || value === null) {
        return [];
    }
    return Array.isArray(value) ? value : [value];
};

/**
 * Returns `true` when a granted role/permission pattern matches a required one.
 * Supports `*` wildcards on the granted side, matching per `:`-delimited segment.
 *
 *   matchesPattern("*", "anything")          -> true
 *   matchesPattern("user:read", "user:read") -> true
 *   matchesPattern("user:*", "user:read")    -> true
 *   matchesPattern("user:read", "user:*")    -> false (wildcards only granted-side)
 */
export const matchesPattern = (granted: string, required: string): boolean => {
    if (granted === required || granted === "*") {
        return true;
    }
    if (granted.indexOf("*") === -1) {
        return false;
    }

    const grantedSegments = granted.split(":");
    const requiredSegments = required.split(":");

    for (let i = 0; i < grantedSegments.length; i++) {
        if (grantedSegments[i] === "*") {
            return true;
        }
        if (grantedSegments[i] !== requiredSegments[i]) {
            return false;
        }
    }

    return grantedSegments.length === requiredSegments.length;
};

/** Whether any granted entry matches the required entry (wildcard-aware). */
const grantedMatches = (granted: string[], required: string): boolean =>
    granted.some((entry) => matchesPattern(entry, required));

/**
 * Pure, synchronous authorization check. This is the single source of truth used
 * by the provider, the `can` helper, the `useAuthorized` hook and `AllowedAccess`.
 */
export const isUserAuthorized = (
    user: IUserPayload | null | undefined,
    query: AccessQuery
): boolean => {
    if (!user) {
        return false;
    }

    const { requireAll = false, check } = query;
    const roles = toArray(query.roles);
    const permissions = toArray(query.permissions);

    const userRoles = user.roles ?? [];
    const userPermissions = user.permissions ?? [];

    const results: boolean[] = [];

    if (roles.length > 0) {
        results.push(
            requireAll
                ? roles.every((role) => grantedMatches(userRoles, role))
                : roles.some((role) => grantedMatches(userRoles, role))
        );
    }

    if (permissions.length > 0) {
        results.push(
            requireAll
                ? permissions.every((permission) =>
                      grantedMatches(userPermissions, permission)
                  )
                : permissions.some((permission) =>
                      grantedMatches(userPermissions, permission)
                  )
        );
    }

    if (typeof check === "function") {
        results.push(check(user));
    }

    if (results.length === 0) {
        return false;
    }

    return requireAll ? results.every(Boolean) : results.some(Boolean);
};
